import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, MoreVertical, ArrowLeft } from 'lucide-react';

// The shared column chrome of the Notes & Task List and Reminders &
// Notifications panels (#437): the module header (badge · title · count · kebab
// · New) and the portaled kebab menu. MOVED verbatim out of NotesPanel.jsx so the
// two new columns (Task List, Notifications) wear exactly the header the Notes
// and Reminders columns already had. The only addition is two accents in
// MODULE_ACCENT (green for tasks, blue for notifications), each used the same
// way the existing two are — a tinted badge, never a filled control except the
// documented New Reminder exception.

// ── Column chrome ───────────────────────────────────────────────────────────

// Kebab (three-dot) column menu — #279.
//
// It REPLACES the old inline "Select" text button so the header has room to grow
// (Past Reminders, #284, is the first tenant; share/archive/pin are the intended
// next ones). PORTALED to document.body with fixed positioning, exactly like the
// ledger Options menu (#249): the column is `overflow-hidden` and lives inside
// transformed panel ancestors, so an in-tree absolute dropdown gets CLIPPED.
// Anchored right-aligned to the trigger and clamped to the viewport on both axes,
// flipping above when there is no room below.
const MENU_WIDTH = 208;
const MENU_EDGE = 8;

export const KebabMenu = ({ buttonTestId, menuTestId, ariaLabel, items }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const anchor = useCallback(() => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const left = Math.max(
            MENU_EDGE,
            Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - MENU_EDGE),
        );
        const below = window.innerHeight - r.bottom - MENU_EDGE;
        const above = r.top - MENU_EDGE;
        const openUp = below < 120 && above > below;
        setPos({
            left,
            top: openUp ? undefined : r.bottom + 4,
            bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
            maxHeight: Math.max(96, openUp ? above : below),
        });
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        // Capture-phase ESC so it closes the MENU, not the panel underneath.
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
        // Scrolling RE-ANCHORS rather than closing (#249): the panel's own lists
        // scroll under the trigger, and closing on any scroll tears the menu out
        // of the DOM mid-interaction. Ignore scrolls from inside the menu itself.
        const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) anchor(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', anchor);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', anchor);
        };
    }, [open, anchor]);

    return (
        <>
            <button
                type="button"
                ref={btnRef}
                data-testid={buttonTestId}
                aria-label={ariaLabel}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => { if (!open) anchor(); setOpen((o) => !o); }}
                className="pai-icon-btn w-[30px] h-[30px] flex-shrink-0"
            >
                <MoreVertical size={16} />
            </button>
            {open && pos && createPortal(
                <div
                    ref={menuRef}
                    data-testid={menuTestId}
                    role="menu"
                    style={{
                        position: 'fixed',
                        left: pos.left,
                        top: pos.top,
                        bottom: pos.bottom,
                        width: MENU_WIDTH,
                        maxHeight: pos.maxHeight,
                    }}
                    className="z-[80] overflow-y-auto py-1 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm"
                >
                    {items.map((it) => (
                        <button
                            key={it.key}
                            type="button"
                            role="menuitem"
                            data-testid={it.testId}
                            data-section={it.section}
                            onClick={() => { setOpen(false); it.onClick(); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-pai-secondary transition-colors hover:bg-white/[0.07] hover:text-white"
                        >
                            {it.icon}
                            <span className="min-w-0 truncate">{it.label}</span>
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </>
    );
};

// Per-module accent. Notes are SAND, Reminders VIOLET — the only two accents on
// this screen, and each one only ever tints a border, an icon or a wash at low
// alpha. The single documented exception is the New Reminder button, which the
// design system names (with the two Kanban label chips) as the one place an
// accent may FILL a control.
export const MODULE_ACCENT = {
    sand: {
        badge: 'border-pai-sand/70 bg-pai-sand/[0.08] text-pai-sand',
        // A dashed, unfilled affordance: "add another one of these".
        newBtn: 'border border-dashed border-pai-strong bg-transparent text-white/60 hover:text-white hover:border-white/40',
    },
    violet: {
        badge: 'border-pai-violet/70 bg-pai-violet/[0.08] text-pai-violet',
        newBtn: 'border border-transparent bg-pai-violet text-[#0B0714] hover:bg-[#9D90F8]',
    },
    // Task List (#437) — the system's own "done" green.
    green: {
        badge: 'border-pai-green/70 bg-pai-green/[0.08] text-pai-green',
        newBtn: 'border border-dashed border-pai-strong bg-transparent text-white/60 hover:text-white hover:border-white/40',
    },
    // Notifications (#437) — the system's informational blue.
    blue: {
        badge: 'border-pai-blue/70 bg-pai-blue/[0.08] text-pai-blue',
        newBtn: 'border border-dashed border-pai-strong bg-transparent text-white/60 hover:text-white hover:border-white/40',
    },
};

export const ColumnHeader = ({
    icon, title, count, accent = 'sand', onNew, newTestId, newLabel,
    selectMode, onCancelSelect, cancelTestId,
    onBack, backTestId, menu, t,
    // Notes & Task List layout chrome (#488), drawn by NotesTasksSplit:
    // `dragProps` make the module badge the drag grip, `layoutControls` (the
    // expand toggle) sits before the kebab. The swap is a kebab item.
    dragProps = null, layoutControls = null,
}) => {
    const tone = MODULE_ACCENT[accent] || MODULE_ACCENT.sand;
    return (
        <div className={`pai-colhead ${layoutControls ? 'pai-colhead-layout ' : ''}flex items-center gap-2.5 px-4 py-3 border-b border-pai-hairline flex-shrink-0`}>
        {onBack ? (
            <button
                type="button"
                data-testid={backTestId}
                onClick={onBack}
                aria-label={t('remindersBack', 'Back')}
                    className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0 -ml-1"
            >
                    <ArrowLeft size={18} />
            </button>
        ) : (
                /* The module badge: 38px, radius 11, a 1.5px accent outline over a
                   faint wash of the same hue. Bordered, never accent-FILLED. */
                <span
                    {...(dragProps || {})}
                    className={`flex items-center justify-center w-[38px] h-[38px] rounded-ctl border-[1.5px] flex-shrink-0 ${tone.badge}${dragProps ? ' cursor-grab active:cursor-grabbing' : ''}`}
                >
                    {icon}
                </span>
        )}
            <div className="flex-1 min-w-0 flex items-baseline gap-2">
                <h3 className="text-[19px] font-bold tracking-[-0.01em] text-white min-w-0 truncate">{title}</h3>
                {typeof count === 'number' && (
                    <span className="pai-colhead-count tnum text-[13px] text-pai-meta flex-shrink-0">{count}</span>
                )}
            </div>
        {/* Leaving select mode stays a one-click affordance in the header — it is a
            MODE EXIT, not a menu action, and burying it would cost two clicks to
            undo one. Only the "Select" ENTRY moved into the kebab (#279). */}
        {selectMode && (
            <button
                type="button"
                data-testid={cancelTestId}
                onClick={onCancelSelect}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded-chip border border-pai text-pai-secondary transition-colors hover:text-white hover:border-pai-strong flex-shrink-0"
            >
                {t('notesSelectDone', 'Cancel')}
            </button>
        )}
        {layoutControls}
        {menu}
        {onNew && (
            <button
                type="button"
                data-testid={newTestId}
                onClick={onNew}
                aria-label={newLabel}
                    className={`inline-flex items-center gap-1.5 h-9 px-3.5 text-[14px] font-bold rounded-ctl transition-colors flex-shrink-0 ${tone.newBtn}`}
            >
                    {/* #432: the label yields (icon-only) when the column header is
                        too narrow to keep the column title readable — see
                        `.pai-colhead` in index.css. */}
                    <Plus size={15} /> <span className="pai-colhead-label">{newLabel}</span>
            </button>
        )}
    </div>
);
};

